import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { apiErrorMessage, suppliersService } from '../../services/api';
import { SupplierStatus } from '../../types';
import { StatusChip } from '../ui';

interface Props {
  shopId?: string;
  onNotice: (message: string) => void;
}

/**
 * Parts supplier accounts. Each supplier declares the credential fields it
 * needs, so a new supplier adapter appears here with its own form and no
 * page changes. Credentials are verified with the supplier before storing.
 */
const SupplierConnections: React.FC<Props> = ({ shopId, onNotice }) => {
  const [suppliers, setSuppliers] = useState<SupplierStatus[]>([]);
  const [forms, setForms] = useState<Record<string, Record<string, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSuppliers(await suppliersService.list(shopId));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load parts suppliers.'));
    }
  }, [shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setField = (provider: string, key: string, value: string) =>
    setForms((current) => ({
      ...current,
      [provider]: { ...current[provider], [key]: value },
    }));

  const connect = async (supplier: SupplierStatus) => {
    setBusy(supplier.provider);
    setError(null);
    try {
      setSuppliers(
        await suppliersService.connect(supplier.provider, forms[supplier.provider] ?? {}, shopId),
      );
      setForms((current) => ({ ...current, [supplier.provider]: {} }));
      onNotice(`${supplier.displayName} connected`);
    } catch (err) {
      setError(apiErrorMessage(err, `Could not connect ${supplier.displayName}.`));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (supplier: SupplierStatus) => {
    setBusy(supplier.provider);
    try {
      await suppliersService.disconnect(supplier.provider, shopId);
      onNotice(`${supplier.displayName} disconnected`);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, `Could not disconnect ${supplier.displayName}.`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {suppliers.map((supplier) => (
        <Paper key={supplier.provider} elevation={0} sx={{ borderRadius: 3, p: 3 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {supplier.displayName}
              </Typography>
              {supplier.connected ? (
                <StatusChip status="active" label="Connected" />
              ) : supplier.status === 'error' ? (
                <StatusChip status="error" />
              ) : (
                <StatusChip status="disconnected" label="Not connected" />
              )}
              {supplier.modes.map((mode) => (
                <Chip
                  key={mode}
                  size="small"
                  variant="outlined"
                  label={mode === 'api' ? 'Search in MechaniX' : "Opens supplier's catalog"}
                />
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {supplier.description}
            </Typography>

            {!supplier.allowed ? (
              <Alert severity="warning">
                Turned off for this shop by the platform administrator.
                {supplier.connected ? ' Your account stays connected and works again once it is turned back on.' : ''}
              </Alert>
            ) : !supplier.available ? (
              <Alert severity="info">
                Not enabled on this server yet — it needs a partner agreement with{' '}
                {supplier.displayName}, then its partner keys in the server configuration.
              </Alert>
            ) : supplier.connected ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {supplier.externalAccountId && (
                  <Typography variant="body2" color="text.secondary">
                    Account {supplier.externalAccountId}
                  </Typography>
                )}
                <Button
                  size="small"
                  color="error"
                  disabled={busy === supplier.provider}
                  onClick={() => disconnect(supplier)}
                >
                  Disconnect
                </Button>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {supplier.lastError && supplier.status === 'error' && (
                  <Alert severity="warning">{supplier.lastError}</Alert>
                )}
                {supplier.credentialFields.map((credential) => (
                  <TextField
                    key={credential.key}
                    size="small"
                    label={credential.label}
                    type={credential.secret ? 'password' : 'text'}
                    autoComplete="off"
                    helperText={credential.help}
                    value={forms[supplier.provider]?.[credential.key] ?? ''}
                    onChange={(event) =>
                      setField(supplier.provider, credential.key, event.target.value)
                    }
                  />
                ))}
                <Box>
                  <Button
                    variant="contained"
                    disabled={
                      busy === supplier.provider ||
                      supplier.credentialFields.some(
                        (credential) => !forms[supplier.provider]?.[credential.key]?.trim(),
                      )
                    }
                    onClick={() => connect(supplier)}
                  >
                    {busy === supplier.provider ? 'Checking…' : `Connect ${supplier.displayName}`}
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default SupplierConnections;
